import { useNavigate } from "@remix-run/react";
import { CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "~/components/ui/button";

export default function FeatureComparisonSection() {
  const navigate = useNavigate();

  return (
    <>
      <section className="container hidden flex-col items-center gap-8 md:flex">
        <div className="flex max-w-[642px] flex-col items-center text-pretty text-center">
          <h2 className="font-recursive text-4xl font-bold leading-normal">
            Supercharge your Knack App
          </h2>
          <p className="font-recursive text-lg">
            Discover how KnackSheet can revolutionize the way you handle and
            interact with your data.
          </p>
        </div>
        <div className="grid w-full auto-rows-min grid-cols-6 grid-rows-[repeat(7,minmax(auto,auto))] rounded border border-emerald-500 bg-neutral-50 p-12 pb-0">
          <h3 className="col-span-3 self-center px-2 font-recursive text-base font-bold uppercase text-neutral-600">
            Features
          </h3>
          <div className="flex items-center justify-center rounded-t bg-neutral-200/50 pt-4">
            <img src="/logo-mark.svg" alt="knack sheet logo" className="h-11" />
          </div>
          <div className="flex items-center justify-center pt-4">
            <img
              src="/excel-logo.png"
              alt="excel logo"
              className="h-11 self-center justify-self-center"
            />
          </div>
          <div className="flex items-center justify-center pt-4">
            <img
              src="/knack-logo.png"
              alt="knack logo"
              className="h-11 self-center justify-self-center"
            />
          </div>
          <div className="col-span-3 flex flex-col border-b border-neutral-300 py-4 pl-2 pr-16">
            <h3 className="font-recursive text-xl font-bold">
              Bulk Data Editing
            </h3>
            <p>
              Update multiple rows or columns of data at once, just like you’re
              used to in an Excel sheet.
            </p>
          </div>
          <div className="flex items-center justify-center border-b border-neutral-300 bg-neutral-200/50">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          </div>
          <div className="flex items-center justify-center border-b border-neutral-300">
            <CheckCircle2 className="h-8 w-8 self-center justify-self-center text-emerald-500" />
          </div>
          <div className="flex items-center justify-center border-b border-neutral-300">
            <XCircle className="h-8 w-8 self-center justify-self-center text-neutral-600" />
          </div>
          <div className="col-span-3 flex flex-col border-b border-neutral-300 py-4 pl-2 pr-16">
            <h3 className="font-recursive text-xl font-bold">
              Drag-and-Drop Data Copying
            </h3>
            <p>
              Quickly replicate data by dragging values across multiple rows and
              columns.
            </p>
          </div>
          <div className="flex items-center justify-center border-b border-neutral-300 bg-neutral-200/50">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          </div>
          <div className="flex items-center justify-center border-b border-neutral-300">
            <CheckCircle2 className="h-8 w-8 self-center justify-self-center text-emerald-500" />
          </div>
          <div className="flex items-center justify-center border-b border-neutral-300">
            <XCircle className="h-8 w-8 self-center justify-self-center text-neutral-600" />
          </div>
          <div className="col-span-3 flex flex-col border-b border-neutral-300 py-4 pl-2 pr-16">
            <h3 className="font-recursive text-xl font-bold">
              Keyboard Shortcuts
            </h3>
            <p>
              Use familiar keyboard shortcuts to speed up data management tasks.
            </p>
          </div>
          <div className="flex items-center justify-center border-b border-neutral-300 bg-neutral-200/50">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          </div>
          <div className="flex items-center justify-center border-b border-neutral-300">
            <CheckCircle2 className="h-8 w-8 self-center justify-self-center text-emerald-500" />
          </div>
          <div className="flex items-center justify-center border-b border-neutral-300">
            <XCircle className="h-8 w-8 self-center justify-self-center text-neutral-600" />
          </div>
          <div className="col-span-3 flex flex-col border-b border-neutral-300 py-4 pl-2 pr-16">
            <h3 className="font-recursive text-xl font-bold">
              Role-based Data Permissions{" "}
            </h3>
            <p>
              Keep your Knack app’s data access permissions, ensuring that
              sensitive data is only available to those who need it.
            </p>
          </div>
          <div className="flex items-center justify-center border-b border-neutral-300 bg-neutral-200/50">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          </div>
          <div className="flex items-center justify-center border-b border-neutral-300">
            <XCircle className="h-8 w-8 self-center justify-self-center text-neutral-600" />
          </div>
          <div className="flex items-center justify-center border-b border-neutral-300">
            <CheckCircle2 className="h-8 w-8 self-center justify-self-center text-emerald-500" />
          </div>
          <div className="col-span-3 flex flex-col px-2 py-4">
            <h3 className="font-recursive text-xl font-bold">
              Web-based Access
            </h3>
            <p>
              Provide an easy-to-learn interface that gets users on board and
              keeps them using the Knack app you worked to build.
            </p>
          </div>
          <div className="flex items-center justify-center rounded-b bg-neutral-200/50">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          </div>
          <div className="flex items-center justify-center">
            <XCircle className="h-8 w-8 self-center justify-self-center text-neutral-600" />
          </div>
          <div className="flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 self-center justify-self-center text-emerald-500" />
          </div>
          <div className="col-span-3" />
          <div className="col-span-1 flex items-center justify-center py-8">
            <Button
              className="h-fit w-full px-8 py-3 text-lg font-semibold leading-normal"
              onClick={() => navigate("/login?try")}
            >
              Try now
            </Button>
          </div>
        </div>
      </section>
      <section className="container flex flex-col items-center gap-8 md:hidden">
        <div className="flex max-w-[642px] flex-col items-center text-pretty text-center">
          <h2 className="font-recursive text-4xl font-bold leading-normal">
            Supercharge your Knack App
          </h2>
          <p className="font-recursive text-lg">
            Discover how KnackSheet can revolutionize the way you handle and
            interact with your data.
          </p>
        </div>
        <div className="flex w-full flex-col rounded border border-emerald-500 bg-neutral-50 px-4 py-6">
          <h3 className="px-4 font-recursive text-base font-bold uppercase text-neutral-600">
            Features
          </h3>
          <div className="flex flex-col gap-6 border-b border-neutral-300 px-4 py-6">
            <div className="col-span-3 flex flex-col">
              <h3 className="font-recursive text-xl font-bold">
                Bulk Data Editing
              </h3>
              <p>
                Update multiple rows or columns of data at once, just like
                you’re used to in an Excel sheet.
              </p>
            </div>
            <div className="flex justify-between">
              <div className="flex flex-col items-start gap-1">
                <p className="font-recursive text-sm font-medium">KnackSheet</p>
                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              </div>
              <div className="flex flex-col items-start gap-1">
                <p className="font-recursive text-sm font-medium">Excel</p>
                <CheckCircle2 className="h-6 w-6 self-center justify-self-center text-emerald-500" />
              </div>
              <div className="flex flex-col items-start gap-1">
                <p className="font-recursive text-sm font-medium">Knack</p>
                <XCircle className="h-6 w-6 self-center justify-self-center text-neutral-600" />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-6 border-b border-neutral-300 px-4 py-6">
            <div className="col-span-3 flex flex-col">
              <h3 className="font-recursive text-xl font-bold">
                Drag-and-Drop Data Copying
              </h3>
              <p>
                Quickly replicate data by dragging values across multiple rows
                and columns.
              </p>
            </div>
            <div className="flex justify-between">
              <div className="flex flex-col items-start gap-1">
                <p className="font-recursive text-sm font-medium">KnackSheet</p>
                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              </div>
              <div className="flex flex-col items-start gap-1">
                <p className="font-recursive text-sm font-medium">Excel</p>
                <CheckCircle2 className="h-6 w-6 self-center justify-self-center text-emerald-500" />
              </div>
              <div className="flex flex-col items-start gap-1">
                <p className="font-recursive text-sm font-medium">Knack</p>
                <XCircle className="h-6 w-6 self-center justify-self-center text-neutral-600" />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-6 border-b border-neutral-300 px-4 py-6">
            <div className="col-span-3 flex flex-col">
              <h3 className="font-recursive text-xl font-bold">
                Keyboard Shortcuts
              </h3>
              <p>
                Use familiar keyboard shortcuts to speed up data management
                tasks.
              </p>
            </div>
            <div className="flex justify-between">
              <div className="flex flex-col items-start">
                <p className="font-recursive text-sm font-medium">KnackSheet</p>
                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              </div>
              <div className="flex flex-col items-start gap-1">
                <p className="font-recursive text-sm font-medium">Excel</p>
                <CheckCircle2 className="h-6 w-6 self-center justify-self-center text-emerald-500" />
              </div>
              <div className="flex flex-col items-start gap-1">
                <p className="font-recursive text-sm font-medium">Knack</p>
                <XCircle className="h-6 w-6 self-center justify-self-center text-neutral-600" />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-6 border-b border-neutral-300 px-4 py-6">
            <div className="col-span-3 flex flex-col">
              <h3 className="font-recursive text-xl font-bold">
                Role-based Data Permissions
              </h3>
              <p>
                Keep your Knack app’s data access permissions, ensuring that
                sensitive data is only available to those who need it.
              </p>
            </div>
            <div className="flex justify-between">
              <div className="flex flex-col items-start gap-1">
                <p className="font-recursive text-sm font-medium">KnackSheet</p>
                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              </div>
              <div className="flex flex-col items-start gap-1">
                <p className="font-recursive text-sm font-medium">Excel</p>
                <XCircle className="h-6 w-6 self-center justify-self-center text-neutral-600" />
              </div>
              <div className="flex flex-col items-start gap-1">
                <p className="font-recursive text-sm font-medium">Knack</p>
                <CheckCircle2 className="h-6 w-6 self-center justify-self-center text-emerald-500" />
              </div>
            </div>
          </div>
          <div>
            <div className="flex flex-col gap-6 border-b border-neutral-300 px-4 py-6">
              <div className="col-span-3 flex flex-col">
                <h3 className="font-recursive text-xl font-bold">
                  Web-based Access
                </h3>
                <p>
                  Provide an easy-to-learn interface that gets users on board
                  and keeps them using the Knack app you worked to build.
                </p>
              </div>
              <div className="flex justify-between">
                <div className="flex flex-col items-start gap-1">
                  <p className="font-recursive text-sm font-medium">
                    KnackSheet
                  </p>
                  <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <p className="font-recursive text-sm font-medium">Excel</p>
                  <XCircle className="h-6 w-6 self-center justify-self-center text-neutral-600" />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <p className="font-recursive text-sm font-medium">Knack</p>
                  <CheckCircle2 className="h-6 w-6 self-center justify-self-center text-emerald-500" />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start pt-8">
            <Button
              className="h-fit w-full px-8 py-3 text-lg font-semibold leading-normal"
              onClick={() => navigate("/login?try")}
            >
              Try for free <ArrowRight className="ml-1 h-6 w-6" />
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
